import React from 'react';
import { Pencil } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { TenderDropzone } from './TenderDropzone';
import { TenderCustomerForm, type TenderCustomerFields } from './TenderCustomerForm';
import type { AusschreibungDetail, TenderDocument } from '../types';

export interface TenderBasisCustomerEditProps {
  values: TenderCustomerFields;
  onChange: (patch: Partial<TenderCustomerFields>) => void;
  selectedContactId: string | null;
  newContactChosen: boolean;
  onContactModeChange: (next: { selectedContactId: string | null; newContactChosen: boolean }) => void;
  contactPickError?: string;
  onPersist: () => Promise<boolean>;
  onDiscardDraft: () => void;
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  }
  return iso;
};

/** Tab „Bauvorhaben“: Kunde und Fristen; „Bearbeiten“ öffnet das Formular direkt in der Spalte. */
export const TenderBauvorhabenPanel: React.FC<{
  ausschreibung: AusschreibungDetail;
  customerEdit?: TenderBasisCustomerEditProps;
}> = ({ ausschreibung, customerEdit }) => {
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const showCustomerEdit = Boolean(customerEdit);

  React.useEffect(() => {
    setEditing(false);
  }, [ausschreibung.id]);

  const cancelEdit = () => {
    customerEdit?.onDiscardDraft();
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!customerEdit) return;
    setSaving(true);
    try {
      const ok = await customerEdit.onPersist();
      if (ok) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const customerNameTrim = ausschreibung.customer_name?.trim() ?? '';
  const customerCompanyTrim = ausschreibung.customer_company?.trim() ?? '';
  const showCompanyLine =
    customerCompanyTrim.length > 0 && customerCompanyTrim !== customerNameTrim;

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden bg-app-surface">
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3">
        {editing && customerEdit ? (
          <div className="space-y-4">
            <TenderCustomerForm
              values={customerEdit.values}
              onChange={customerEdit.onChange}
              selectedContactId={customerEdit.selectedContactId}
              newContactChosen={customerEdit.newContactChosen}
              onContactModeChange={customerEdit.onContactModeChange}
              contactPickError={customerEdit.contactPickError}
              narrowRail
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void saveEdit()}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-pipedrive-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <LoadingSpinner size="xs" /> : null}
                Speichern
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className="text-base font-semibold text-app-text min-w-0">
                {ausschreibung.customer_name?.trim() ? (
                  ausschreibung.customer_name
                ) : (
                  <span className="italic font-normal text-slate-400">Kein Ansprechpartner</span>
                )}
              </div>
              {showCustomerEdit && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="shrink-0 p-1 rounded text-app-text-subtle hover:bg-app-hover hover:text-pipedrive-blue transition"
                  title="Kunde / Bauvorhaben bearbeiten"
                  aria-label="Kunde / Bauvorhaben bearbeiten"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
            {showCompanyLine && (
              <div className="text-sm text-app-text-muted mt-0.5">{ausschreibung.customer_company}</div>
            )}
            <div className="text-sm text-app-text tabular-nums mt-0.5">
              {[
                ausschreibung.customer_street,
                [ausschreibung.customer_zip, ausschreibung.customer_city].filter(Boolean).join(' '),
              ]
                .filter(Boolean)
                .join(', ') || ''}
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <div>
                <dt className="text-xs text-app-text-subtle">Abgabefrist</dt>
                <dd className="text-app-text tabular-nums">{fmtDate(ausschreibung.deadline)}</dd>
              </div>
              <div>
                <dt className="text-xs text-app-text-subtle">Ausführung</dt>
                <dd className="text-app-text break-words">{ausschreibung.execution || ''}</dd>
              </div>
              <div>
                <dt className="text-xs text-app-text-subtle">Eingang</dt>
                <dd className="text-app-text-muted tabular-nums">{fmtDate(ausschreibung.created_at.slice(0, 10))}</dd>
              </div>
              <div>
                <dt className="text-xs text-app-text-subtle">Bindefrist</dt>
                <dd className="text-app-text-muted tabular-nums">
                  {ausschreibung.binding_period_days != null
                    ? `${ausschreibung.binding_period_days} Tage`
                    : ''}
                </dd>
              </div>
            </dl>
          </>
        )}
      </div>
    </div>
  );
};

/** Tab „Dokumente“: nur Liste; PDF-Modal liegt in der Parent-Leiste. */
export const TenderDocumentsListPanel: React.FC<{
  ausschreibungId: string;
  documents: TenderDocument[];
  onUploaded: (doc: TenderDocument) => void;
  onDeleted: (docId: string) => void;
}> = ({ ausschreibungId, documents, onUploaded, onDeleted }) => {
  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden px-4 py-3 bg-app-surface">
      <div className="min-h-0 flex-1 overflow-hidden">
        <TenderDropzone
          ausschreibungId={ausschreibungId}
          documents={documents}
          onUploaded={onUploaded}
          onDeleted={onDeleted}
        />
      </div>
    </div>
  );
};
